/**
 * Keeps the app-wide roll toasts (`lib/dice/DiceOverlay.tsx`) on the visible stage: between the open
 * rail page's right edge and COMMS's left edge, through the `--lvtt-dice-readout-left/right` CSS
 * variables the toast stack reads. A wide page covers the whole stage, so with one open the toasts
 * centre over the page rather than squeezing to nothing. Cleared when the shell unmounts.
 */
import { useLayoutEffect, type RefObject } from "react";

const LEFT_VAR = "--lvtt-dice-readout-left";
const RIGHT_VAR = "--lvtt-dice-readout-right";
/** Narrower than this and the lane can't hold a toast. */
const MIN_LANE = 280;

/** `pageKey` changes whenever the open page (or its width) does, re-measuring the lane. */
export function useRollToastLane(main: RefObject<HTMLElement | null>, comms: RefObject<HTMLElement | null>, pageKey: string) {
  useLayoutEffect(() => {
    const mainEl = main.current;
    const commsEl = comms.current;
    if (!mainEl || !commsEl) return;
    const root = document.documentElement;
    const measure = () => {
      const mainLeft = mainEl.getBoundingClientRect().left;
      // `offsetWidth`, not the rect: the panel slides in with a transform, which a rect would catch mid-flight.
      const panel = mainEl.querySelector<HTMLElement>(".shell-panel");
      const right = window.innerWidth - commsEl.getBoundingClientRect().left;
      let left = mainLeft + (panel?.offsetWidth ?? 0);
      if (window.innerWidth - right - left < MIN_LANE) left = mainLeft;
      root.style.setProperty(LEFT_VAR, `${left}px`);
      root.style.setProperty(RIGHT_VAR, `${right}px`);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(mainEl);
    ro.observe(commsEl);
    // A closing page leaves the DOM only after its exit animation, after this effect has run.
    const mo = new MutationObserver(measure);
    mo.observe(mainEl, { childList: true });
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", measure);
      root.style.removeProperty(LEFT_VAR);
      root.style.removeProperty(RIGHT_VAR);
    };
  }, [main, comms, pageKey]);
}
